import { useState, useEffect } from "react";
import JoinDonorCommunity from "../modals/post/JoinDonorCom";
import useJoinDonorCom from "../hooks/post/useJoinDonorCom";
import { useTranslation } from "react-i18next";
import { useGetUserMembershipQuery } from "../services/userAuthApi";
import date from "date-and-time";
import { getToken } from "../storage/LocalStorageService";
import { Loading } from "../components/Loading";
import {
  FaShieldAlt,
  FaArrowRight,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import DonorCommunityBenefits from "./benefits-donor-community";
import { motion } from "framer-motion";

const DonorCommunityPage = () => {
  const uJoinDonorCom = useJoinDonorCom();
  const { data: membershipData, isLoading, isError, error } = useGetUserMembershipQuery();
  const [isActive, setIsActive] = useState(false);
  const [formattedStartDate, setFormattedStartDate] = useState("N/A");
  const [formattedEndDate, setFormattedEndDate] = useState("N/A");

  useEffect(() => {
    if (membershipData?.data) {
      setIsActive(membershipData.data.is_active);
      const startDate = membershipData.data.starts;
      const endDate = membershipData.data.ends;

      if (startDate) {
        setFormattedStartDate(date.format(new Date(startDate), "DD MMM YYYY"));
      }
      if (endDate) {
        setFormattedEndDate(date.format(new Date(endDate), "DD MMM YYYY"));
      }
    } else {
      setIsActive(false);
      setFormattedStartDate("N/A");
      setFormattedEndDate("N/A");
    }
  }, [membershipData]);

  const { t } = useTranslation("joinDonor");
  const main = t("main");
  const wchannel = t("MyDonorDashboard");
  const { access_token } = getToken();

  const handleJoinFunction = (plan) => {
    uJoinDonorCom.open(plan);
  };

  if (isLoading) return <Loading />;
  if (isError && error?.status !== 401)
    return <div className="text-center text-red-600 dark:text-red-400">⚠️ Error loading membership data</div>;

  return (
    <>
      {/* SEO Optimized Helmet */}
      <Helmet>
        <title>Join Donor Community</title>
        <meta name="description" content="Become part of a secure donor community. Watch real stories, support verified needies directly, and make a transparent impact. Join today with a simple verification." />
        
        <meta name="keywords" content="Donor community, Support needy, Verified Zakat, Transparent donations, Charity platform, Islamic giving, Needy video proof, Donor dashboard, Join Zakat Donor Network, Verified Zakat Givers, Community, Private Zakat Donors Platform, Trusted Zakat Donation Circle, Exclusive Zakat Support Group,  donate zakat online, give zakat online, pay zakat online, donate zakat pakistan, zakat donation portal, online zakat, zakat pakistan, zakat fund, zakat donations, zakat in islam, muslim zakat, zakat fund in pakistan, who is eligible for fitrana, sadaqah online, fidya rules, fitrana amount, when to pay zakat, how to give zakat in islam, how to pay zakat, when should i pay zakat, 1 lakh par kitni zakat hai, zakat donation reviews, donor feedback, zakat success stories, contact adda zakat, zakat helpline, zakat support, charity contact pakistan, join donor community, Zakat donor platform, Islamic charity membership, verified Zakat donor, AddaZakat community, donor benefits, Zakat verification process, charity platform, support needy with Zakat, direct Zakat transfers, Islamic giving, bank statement for Zakat, payslip for charity, utility bill for Zakat, donor dashboard, Zakat community benefits, trusted charity platform, how to become a Zakat donor, Zakat for the needy, Islamic donor verification, charity community, Sadaqah platform, donor membership details, join AddaZakat, ڈونر کمیونٹی میں شامل ہوں, زکوٰۃ ڈونر پلیٹ فارم, اسلامی خیرات رکنیت, تصدیق شدہ زکوٰۃ ڈونر, ایڈا ذکوٰۃ کمیونٹی, ڈونر کے فوائد, زکوٰۃ کی تصدیق کا عمل, خیراتی پلیٹ فارم, ضرورت مندوں کی زکوٰۃ سے مدد, براہ راست زکوٰۃ ٹرانسفر, اسلامی خیرات, زکوٰۃ کے لیے بینک اسٹیٹمنٹ, خیرات کے لیے تنخواہ کی پرچی, زکوٰۃ کے لیے یوٹیلٹی بل, ڈونر ڈیش بورڈ, زکوٰۃ کمیونٹی کے فوائد, قابل اعتماد خیراتی پلیٹ فارم, زکوٰۃ ڈونر کیسے بنیں, ضرورت مندوں کے لیے زکوٰۃ, اسلامی ڈونر کی تصدیق, خیراتی کمیونٹی, صدقہ پلیٹ فارم, ڈونر رکنیت کی تفصیلات, ایڈا ذکوٰۃ میں شامل ہوں, الانضمام إلى مجتمع المتبرعين, منصة الزكاة للمتبرعين, عضوية الصدقة الإسلامية, متبرع زكاة موثق, مجتمع أضازكاة, فوائد المتبرعين, عملية توثيق الزكاة, منصة الصدقة, دعم المحتاجين بالزكاة, تحويلات الزكاة المباشرة, الصدقة الإسلامية, كشف حساب بنكي للزكاة, إيصال راتب للصدقة, فاتورة مرافق للزكاة, لوحة تحكم المتبرعين, فوائد مجتمع الزكاة, منصة صدقة موثوقة, كيفية أن تصبح متبرع زكاة, الزكاة للمحتاجين, توثيق المتبرعين الإسلاميين, مجتمع الصدقة, منصة الصدقة, تفاصيل عضوية المتبرعين, انضم إلى أضازكاة" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://addazakat.com/donor-community" />

        {/* Open Graph for Social Sharing */}
        <meta property="og:title" content="Join Our Trusted Donor Community | Support Verified Needy" />
        <meta property="og:description" content="Watch videos, view documents, and support verified needies through secure direct transfers. Become a valued member of our impact-driven donor community." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://addazakat.com/donor-community" />
        <meta property="og:image" content="https://addazakat.com/az-logo.png" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Join Our Trusted Donor Community | Support Verified Needy" />
        <meta name="twitter:description" content="Verified community for transparent Zakat and donations. See stories, view proofs, and contribute directly to real people." />
        <meta name="twitter:image" content="https://addazakat.com/az-logo.png" />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Donor Community",
            "url": "https://addazakat.com/donor-community",
            "description": "Join a secure and verified donor community. Access real videos and documents of needies and contribute directly. Build trust and transparency through impactful giving.",
            "publisher": {
              "@type": "Organization",
              "name": "AddaZakat",
              "url": "https://addazakat.com"
            }
          })}
        </script>

      </Helmet>

      <main className="flex items-center min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:via-emerald-900 dark:to-teal-900 px-4 md:px-12 py-12">
        <div className="mx-auto max-w-6xl w-full">

          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14 mt-10"
          >
            <h1 className="text-4xl md:text-6xl font-black bg-gradient-to-r from-blue-600 via-green-800 dark:via-green-100 to-indigo-400 bg-clip-text text-transparent mb-6">
              {main.heading}
            </h1>
            <p className="text-lg md:text-2xl text-gray-600 dark:text-gray-300 font-medium max-w-2xl mx-auto leading-relaxed">
              {main.sub_heading}
            </p>
          </motion.div>

          {/* Benefits */}
          <DonorCommunityBenefits />

          {/* Membership Details / Join Options */}
          <div className="mt-16">
            {isActive && access_token ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                className="p-6 max-w-lg mx-auto bg-white/90 dark:bg-gray-800 rounded-2xl shadow-xl relative"
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-purple-500/20 rounded-full blur-3xl" />
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-800 dark:text-white flex items-center">
                    <FaShieldAlt className="text-purple-600 dark:text-purple-400 mr-2" />
                    {wchannel.detail}
                  </h2>
                  <span className="text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-700 dark:text-purple-100 px-3 py-1 rounded-full">
                    {main.member}
                  </span>
                </div>

                {/* Membership Info */}
                <dl className="space-y-4 text-sm">
                  <InfoRow label={wchannel.plan} value={membershipData?.data?.plan} />
                  <InfoRow
                    label={wchannel.status}
                    value={isActive ? wchannel.active : wchannel.inactive}
                    badge={isActive ? "success" : "danger"}
                  />
                  <InfoRow label={wchannel.start} value={formattedStartDate} />
                  <InfoRow label={wchannel.end} value={formattedEndDate} />
                </dl>
              </motion.div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-center text-gray-800 dark:text-white mb-10">
                  {main.joining_req}
                </h3>


                {/* Plans Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <PlanCard
                    title="3 Month Free Membership"
                    description={main.social_requirements}
                    duration={main.utility_bill_title}
                    gradient="from-blue-500 to-indigo-500"
                    onJoin={() => handleJoinFunction("3m")}
                    main={main}
                  />
                  <PlanCard
                    title="12 Month Free Membership"
                    description={main.bank_requirements}
                    duration={main.bank_title}
                    gradient="from-amber-500 to-orange-500"
                    onJoin={() => handleJoinFunction("12m")}
                    main={main}
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <JoinDonorCommunity />
      </main>
    </>
  );
};

export default DonorCommunityPage;

/* ---------------------- Sub Components ---------------------- */

const InfoRow = ({ label, value, badge }) => {
  const badgeClass =
    badge === "success"
      ? "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100"
      : badge === "danger"
      ? "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100"
      : "";
  return (
    <div className="flex justify-between items-center border-b pb-2 border-dashed border-gray-300 dark:border-gray-700">
      <span className="text-gray-600 dark:text-gray-400 font-medium">{label}</span>
      {badge ? (
        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badgeClass}`}>{value}</span>
      ) : (
        <span className="font-semibold text-gray-800 dark:text-white">{value}</span>
      )}
    </div>
  );
};

const PlanCard = ({ title, description, duration, gradient, onJoin, main }) => (
  <motion.div
    whileHover={{ y: -6, scale: 1.02 }}
    transition={{ duration: 0.2 }}
    className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-md hover:shadow-2xl transition p-6 flex flex-col justify-between border border-gray-200 dark:border-gray-700"
  >
    <h3 className="text-lg font-bold text-gray-800 dark:text-white mb-2">{title}</h3>
    <p className="text-sm font-semibold text-gray-500 dark:text-gray-300 mb-2">{duration}</p>

    <div className="text-gray-600 dark:text-gray-300 text-sm space-y-2 mb-6">
      {Array.isArray(description)
        ? description.map((req, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              {req}
            </div>
          ))
        : description}
    </div>

    <button
      onClick={onJoin}
      className={`group relative inline-flex items-center justify-center px-6 py-2 text-sm font-semibold rounded-xl text-white bg-gradient-to-r ${gradient} hover:opacity-90 transition-all`}
    >
      {main.join}
      <FaArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
    </button>
  </motion.div>
);
